import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeedbackUrlParams } from './useFeedbackUrlParams';

import { useSettingsStore, STORAGE_KEY } from '@/stores/settingsStore';
import type { Language, Theme } from '@/types/settings';

const VALID_THEMES: Theme[] = ['light', 'dark', 'system'];
const VALID_LANGUAGES: Language[] = ['en', 'zh-CN'];

function detectBrowserLanguage(): Language | null {
  if (typeof navigator === 'undefined') return null;

  const browserLanguages = navigator.languages || [navigator.language];

  for (const browserLang of browserLanguages) {
    const normalizedLang = browserLang.toLowerCase();

    if (normalizedLang.startsWith('zh')) {
      if (normalizedLang.includes('cn') || normalizedLang === 'zh') {
        return 'zh-CN';
      }
    }

    if (normalizedLang.startsWith('en')) {
      return 'en';
    }
  }

  return null;
}

function hasStoredLanguage(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return false;

  try {
    const parsed = JSON.parse(stored);
    return Boolean(parsed.state?.language);
  } catch {
    return false;
  }
}

export function useSettingsInitializer(): void {
  const { i18n } = useTranslation();
  const { theme: urlTheme, lang: urlLang } = useFeedbackUrlParams();
  const language = useSettingsStore((state) => state.language);
  const setLanguage = useSettingsStore((state) => state.setLanguage);
  const setTheme = useSettingsStore((state) => state.setTheme);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (urlTheme && VALID_THEMES.includes(urlTheme as Theme)) {
      setTheme(urlTheme as Theme);
    }

    if (urlLang && VALID_LANGUAGES.includes(urlLang as Language)) {
      setLanguage(urlLang as Language);
      i18n.changeLanguage(urlLang as Language);
      return;
    }

    if (!hasStoredLanguage()) {
      const detectedLanguage = detectBrowserLanguage();
      if (detectedLanguage && detectedLanguage !== language) {
        setLanguage(detectedLanguage);
        i18n.changeLanguage(detectedLanguage);
      }
    }
  }, [urlTheme, urlLang, language, setLanguage, setTheme, i18n]);
}
